import type { FormikHelpers } from "formik";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import ActionCreator from "metabase/actions/containers/ActionCreator/ActionCreator";
import ActionParametersInputForm, {
  ActionParametersInputModal,
} from "metabase/actions/containers/ActionParametersInputForm";
import { useActionInitialValues } from "metabase/actions/hooks/use-action-initial-values";
import { getFormTitle, isImplicitUpdateAction } from "metabase/actions/utils";
import { Modal } from "metabase/common/components/Modal";
import { getDashboardType } from "metabase/lib/dashboard";
import { ActionsApi, PublicApi } from "metabase/services";
import type {
  ActionDashboardCard,
  Dashboard,
  OnSubmitActionForm,
  Parameter,
  ParameterId,
  ParametersForActionExecution,
  VisualizationSettings,
  WritebackAction,
  WritebackParameter,
} from "metabase-types/api";

import ActionButtonView from "./ActionButtonView";
import { FormTitle, FormWrapper } from "./ActionForm.styled";
import {
  getParamValuesFromUrl,
  shouldAutoOpenAction,
  shouldAutoSubmitAction,
  shouldShowConfirmation,
} from "./utils";

export interface ActionFormProps {
  action: WritebackAction;
  dashcard: ActionDashboardCard;
  dashboard: Dashboard;
  missingParameters?: WritebackParameter[];
  mappedParameters?: WritebackParameter[];
  dashboardParameters?: Record<ParameterId, Parameter>;
  allDashboardParameters?: Parameter[];
  dashcardParamValues: ParametersForActionExecution;
  settings: VisualizationSettings;
  isSettings: boolean;
  shouldDisplayButton: boolean;
  isEditingDashcard: boolean;
  canEditAction: boolean | undefined;
  onSubmit: OnSubmitActionForm;

  onActionEdit?: (newAction: WritebackAction) => void;
}

function AutoSubmitPopup({
  message,
  isError,
  isSuccess,
}: {
  message: string;
  isError?: boolean;
  isSuccess?: boolean;
}) {
  const [showPopup, setShowPopup] = useState(true);

  // useEffect(() => {
  //   const timer = setTimeout(() => {
  //     setShowPopup(false);
  //   }, 3000); // Hide the popup after 3 seconds

  //   return () => clearTimeout(timer);
  // }, []);
  
  if (!showPopup) {
    return null;
  }
  return (
    <Modal isOpen={showPopup} onClose={() => setShowPopup(false)}>
      <h1>{isError ? "Error" : isSuccess ? "Success" : "Info"}</h1>
      <p>{message}</p>
    </Modal>
  );
}

function ActionVizForm({
  action,
  dashcard,
  dashboard,
  settings,
  missingParameters = [],
  mappedParameters = [],
  dashboardParameters,
  allDashboardParameters,
  dashcardParamValues,
  isSettings,
  shouldDisplayButton,
  isEditingDashcard,
  canEditAction,
  onSubmit,

  onActionEdit,
}: ActionFormProps) {
  const [showFormModal, setShowFormModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [autoSubmitMessage, setAutoSubmitMessage] = useState<string | undefined>(undefined);
  const [autoSubmitStatus, setAutoSubmitStatus] = useState<"success" | "error" | undefined>(undefined);
  const title = getFormTitle(action);

  // only show confirmation if there are no missing parameters
  const showConfirmMessage =
    shouldShowConfirmation(action) && missingParameters.length === 0;

  const onClick = () => {
    setShowFormModal(true);
  };

  // Auto-open the form modal when #openAction=<actionId> is in the URL
  useEffect(() => {
    if (shouldAutoOpenAction(action)) {
      setShowFormModal(true);
    }
  }, [action]);

  const onModalSubmit = useCallback(
    async (params: ParametersForActionExecution) => {
      const result = await onSubmit(params);
      if (result.success) {
        setShowFormModal(false);
        setAutoSubmitMessage(result.message || "submitted successfully.");
      }
      result && setAutoSubmitMessage(result.message);
      window.location.hash = "";
      return result;
    },
    [onSubmit],
  );

  const handleActionEdit = () => {
    setShowEditModal(true);
  };

  const closeEditModal = () => {
    setShowEditModal(false);
  };

  const fetchInitialValues = useCallback(async () => {
    const prefetchDashcardValues =
      getDashboardType(dashboard.id) === "public"
        ? PublicApi.prefetchDashcardValues
        : ActionsApi.prefetchDashcardValues;

    const canPrefetch = Object.keys(dashcardParamValues).length > 0;

    if (!canPrefetch) {
      return {};
    }

    return prefetchDashcardValues({
      dashboardId: dashboard.id,
      dashcardId: dashcard.id,
      parameters: JSON.stringify(dashcardParamValues),
    });
  }, [dashboard.id, dashcard.id, dashcardParamValues]);

  const shouldPrefetch = isImplicitUpdateAction(action);

  const { hasPrefetchedValues, initialValues, prefetchValues } =
    useActionInitialValues({
      fetchInitialValues,
      initialValues: dashcardParamValues,
      shouldPrefetch,
    });

  const handleSubmitSuccess = useCallback(
    (actions: FormikHelpers<ParametersForActionExecution>) => {
      if (shouldPrefetch) {
        prefetchValues();
      } else {
        actions.resetForm();
      }
    },
    [shouldPrefetch, prefetchValues],
  );
  const urlParamValues = useMemo(() => getParamValuesFromUrl(action), [action]);

  const mergedInitialValues = useMemo(
    () => ({ ...initialValues, ...urlParamValues }),
    [initialValues, urlParamValues],
  );

  const hasAutoSubmittedRef = useRef(false);

  // Auto-submit the form when #openAction=<actionId>&autoSubmit=true is in the URL,
  // unless the action requires confirmation.
  useEffect(() => {
    if (
      hasAutoSubmittedRef.current ||
      showConfirmMessage ||
      !shouldAutoSubmitAction(action)
    ) {
      return;
    }
    if (shouldPrefetch && !hasPrefetchedValues) {
      return;
    }
    hasAutoSubmittedRef.current = true;
    setShowFormModal(false);
    onModalSubmit(mergedInitialValues);
  }, [
    action,
    showConfirmMessage,
    shouldPrefetch,
    hasPrefetchedValues,
    mergedInitialValues,
    onModalSubmit,
  ]);

  if (shouldDisplayButton) {
    return (
      <>
        <ActionButtonView
          settings={settings}
          isFullHeight={!isSettings}
          focus={isEditingDashcard}
          onClick={onClick}
        />
        {showFormModal && (
          <ActionParametersInputModal
            action={action}
            mappedParameters={mappedParameters}
            dashboardId={dashboard.id}
            dashboardParameters={dashboardParameters}
            allDashboardParameters={allDashboardParameters}
            initialValues={mergedInitialValues}
            title={title}
            showEmptyState={shouldPrefetch && !hasPrefetchedValues}
            showConfirmMessage={showConfirmMessage}
            confirmMessage={action.visualization_settings?.confirmMessage}
            onEdit={canEditAction ? handleActionEdit : undefined}
            onSubmit={onModalSubmit}
            onSubmitSuccess={handleSubmitSuccess}
            onClose={() => {
              setShowFormModal(false);
              window.location.hash = "";
            }}
            onCancel={() => setShowFormModal(false)}
          />
        )}
        {autoSubmitMessage && (
          <AutoSubmitPopup
            message={autoSubmitMessage}
            isError={autoSubmitStatus === "error"}
            isSuccess={autoSubmitStatus === "success"}
          />
        )}
        {showEditModal && (
          <Modal
            wide
            data-testid="action-editor-modal"
            onClose={closeEditModal}
          >
            <ActionCreator
              initialAction={action}
              action={action}
              modelId={action.model_id}
              databaseId={action.database_id}
              actionId={action.id}
              onSubmit={onActionEdit}
              onClose={closeEditModal}
            />
          </Modal>
        )}
      </>
    );
  }

  return (
    <FormWrapper>
      <FormTitle>{title}</FormTitle>
      <ActionParametersInputForm
        action={action}
        mappedParameters={mappedParameters}
        dashboardId={dashboard.id}
        dashboardParameters={dashboardParameters}
        allDashboardParameters={allDashboardParameters}
        initialValues={mergedInitialValues}
        onSubmit={onSubmit}
        onSubmitSuccess={handleSubmitSuccess}
      />
    </FormWrapper>
  );
}

// eslint-disable-next-line import/no-default-export -- deprecated usage
export default ActionVizForm;
